/**
 * Word-level diff for brand field proposals.
 *
 * Brand fields are a few hundred words, so a plain LCS table is cheap and
 * avoids pulling in a diff dependency for one screen.
 */

const SEGMENT_EQUAL = "equal";
const SEGMENT_ADD = "add";
const SEGMENT_REMOVE = "remove";

/** Words plus their trailing whitespace, so joining segments restores the text. */
const tokenize = (value = "") => String(value ?? "").match(/\S+\s*/g) || [];

export const asDiffText = (value) => {
  if (Array.isArray(value)) {
    return value.join("\n");
  }
  return String(value ?? "");
};

/**
 * @returns {Array<{type: "equal"|"add"|"remove", text: string}>}
 */
export const diffWords = (currentValue, proposedValue) => {
  const current = tokenize(asDiffText(currentValue));
  const proposed = tokenize(asDiffText(proposedValue));

  /* lcs[i][j] = longest common subsequence of current[i:] and proposed[j:] */
  const lcs = Array.from({ length: current.length + 1 }, () =>
    new Uint32Array(proposed.length + 1)
  );
  for (let i = current.length - 1; i >= 0; i -= 1) {
    for (let j = proposed.length - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        current[i].trim() === proposed[j].trim()
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const segments = [];
  const push = (type, text) => {
    const last = segments[segments.length - 1];
    if (last && last.type === type) {
      last.text += text;
      return;
    }
    segments.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < current.length && j < proposed.length) {
    if (current[i].trim() === proposed[j].trim()) {
      push(SEGMENT_EQUAL, proposed[j]);
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push(SEGMENT_REMOVE, current[i]);
      i += 1;
    } else {
      push(SEGMENT_ADD, proposed[j]);
      j += 1;
    }
  }
  while (i < current.length) {
    push(SEGMENT_REMOVE, current[i]);
    i += 1;
  }
  while (j < proposed.length) {
    push(SEGMENT_ADD, proposed[j]);
    j += 1;
  }

  return segments;
};

export const hasChanges = (segments = []) =>
  segments.some((segment) => segment.type !== SEGMENT_EQUAL);

/**
 * Collapses untouched stretches so the reader sees only what changed, keeping
 * `contextWords` around each edit. Collapsed runs become `{ collapsed: true }`.
 */
export const collapseUnchanged = (segments = [], contextWords = 12) =>
  segments.reduce((accumulator, segment, index) => {
    if (segment.type !== SEGMENT_EQUAL) {
      accumulator.push(segment);
      return accumulator;
    }

    const words = tokenize(segment.text);
    const isFirst = index === 0;
    const isLast = index === segments.length - 1;
    /* leading/trailing runs only need context on the side facing an edit */
    const head = isFirst ? 0 : contextWords;
    const tail = isLast ? 0 : contextWords;

    if (words.length <= head + tail) {
      accumulator.push(segment);
      return accumulator;
    }

    if (head) {
      accumulator.push({ type: SEGMENT_EQUAL, text: words.slice(0, head).join("") });
    }
    accumulator.push({
      type: SEGMENT_EQUAL,
      collapsed: true,
      hiddenWords: words.length - head - tail,
    });
    if (tail) {
      accumulator.push({
        type: SEGMENT_EQUAL,
        text: words.slice(words.length - tail).join(""),
      });
    }
    return accumulator;
  }, []);

export const DIFF_SEGMENT_EQUAL = SEGMENT_EQUAL;
export const DIFF_SEGMENT_ADD = SEGMENT_ADD;
export const DIFF_SEGMENT_REMOVE = SEGMENT_REMOVE;
